import * as React from 'react'
import type { JSX } from 'react'

import {
  Badge,
  Button,
  Chips,
  ChevronDownIcon,
  ChevronRightIcon,
  Drawer,
  DropdownMenu,
  EmptyState,
  Flex,
  Heading,
  IconButton,
  Input,
  ListFilterIcon,
  ScrollArea,
  SearchIcon,
  Skeleton,
  Text,
  useIsInDrawer,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import { getSlotIds } from '~publish/helpers/getSlotIds'

import { TemplatesFilterCombobox } from '../TemplatesFilterCombobox'
import type {
  TemplatesFilterComboboxGroupProps,
  TemplatesFilterComboboxProps,
} from '../TemplatesFilterCombobox'

import styles from './TemplateLibraryPage.module.css'

export const NoTemplatesFound = ({
  clearFilters,
}: {
  clearFilters: () => void
}): JSX.Element => {
  return (
    <EmptyState size="medium" className={styles.emptyState}>
      <EmptyState.Icon>
        <SearchIcon />
      </EmptyState.Icon>
      <EmptyState.Heading as="h2" size="small">
        No templates found
      </EmptyState.Heading>
      <EmptyState.Description>
        Try adjusting your filters or try a different search.
      </EmptyState.Description>
      <Button onClick={clearFilters}>Clear Filters</Button>
    </EmptyState>
  )
}

export const TemplatesLoadingGrid = (): JSX.Element => {
  return (
    <div className={styles.templateGrid}>
      {getSlotIds('template-loading-skeleton', 24).map((skeletonId) => (
        <Skeleton key={skeletonId} height={120} />
      ))}
    </div>
  )
}

type LibraryTemplateGridProps<TTemplate> = {
  templates: TTemplate[]
  emptyState: JSX.Element
  children: (template: TTemplate) => JSX.Element
}

export const LibraryTemplateGrid = <TTemplate,>({
  templates,
  emptyState,
  children,
}: LibraryTemplateGridProps<TTemplate>): JSX.Element => {
  if (templates.length === 0) return emptyState

  return (
    <div className={styles.templateGrid}>
      {templates.map((template) => children(template))}
      {/* Fill empty grid items so a 1–3 item grid doesn't stretch its cards. */}
      {getSlotIds(
        'template-grid-placeholder',
        Math.max(0, 4 - templates.length),
      ).map((placeholderId) => (
        <div key={placeholderId} className={styles.emptyGridItem} />
      ))}
    </div>
  )
}

export const TemplateSections = ({
  children,
}: {
  children: React.ReactNode
}): JSX.Element => <div className={styles.sections}>{children}</div>

// Number of cards shown in a section row before "See all" is needed.
const SECTION_PREVIEW_COUNT = 10

type TemplateSectionProps<TTemplate extends { id: string }> = {
  title: string
  templates: TTemplate[]
  onSeeAll: () => void
  children: (template: TTemplate) => JSX.Element
  /**
   * Optional content rendered between the section header and the cards row.
   * Used by the primary-campaign section to inline the campaign banner so it
   * sits beside its own templates instead of sitting up in the page header.
   */
  banner?: React.ReactNode
}

export const TemplateSection = <TTemplate extends { id: string }>({
  title,
  templates,
  onSeeAll,
  banner,
  children,
}: TemplateSectionProps<TTemplate>): JSX.Element => {
  const preview = templates.slice(0, SECTION_PREVIEW_COUNT)

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <Heading as="h2" size="sm">
          {title}
        </Heading>
        <Button
          variant="tertiary"
          size="small"
          onClick={onSeeAll}
          aria-label={`See all ${title}`}
        >
          See All
          <ChevronRightIcon />
        </Button>
      </div>
      {banner}
      <ScrollArea scrollbars="horizontal" visibility="none" gradientFade={40}>
        <div className={styles.sectionRow}>
          {preview.map((template) => (
            <div key={template.id} className={styles.sectionCard}>
              {children(template)}
            </div>
          ))}
        </div>
      </ScrollArea>
    </section>
  )
}

export const NarrowMenuTitle = ({
  children,
}: {
  children: React.ReactNode
}): JSX.Element | null => {
  const isInDrawer = useIsInDrawer()
  if (!isInDrawer) return null
  return (
    <VisuallyHidden>
      <Drawer.Title>{children}</Drawer.Title>
    </VisuallyHidden>
  )
}

export type TemplateFilterOption = {
  value: string
  label: React.ReactNode
  chipLabel?: React.ReactNode
}

export type TemplateFilterGroup = {
  heading: string
  filterType: TemplatesFilterComboboxGroupProps['filterType']
  options: TemplateFilterOption[]
}

export const toTemplateFilterOptions = (
  values: readonly string[],
): TemplateFilterOption[] => values.map((value) => ({ value, label: value }))

const NarrowFilterChipGroup = ({
  label,
  options,
  filters,
  onFiltersChange,
}: {
  label: string
  options: TemplateFilterOption[]
  filters: string[]
  onFiltersChange: (next: string[]) => void
}): JSX.Element => {
  const values = new Set(options.map((option) => option.value))
  const selection = filters.filter((f) => values.has(f))
  const handleChange = (next: string[]): void => {
    const other = filters.filter((f) => !values.has(f))
    onFiltersChange([...other, ...next])
  }

  return (
    <div className={styles.narrowFilterGroup}>
      <Text size="sm" weight="medium" color="subtle">
        {label}
      </Text>
      <Chips type="multiple" value={selection} onChange={handleChange}>
        {options.map((option) => (
          <Chips.Item key={option.value} value={option.value}>
            {option.chipLabel ?? option.label}
          </Chips.Item>
        ))}
      </Chips>
    </div>
  )
}

type TemplatesSearchAndFilterProps = {
  query: string
  filters: string[]
  groups: TemplateFilterGroup[]
  showFilters: boolean
  cta: TemplatesFilterComboboxProps['cta']
  onQueryChange: (query: string) => void
  onFiltersChange: (filters: string[]) => void
  onReset: () => void
}

/**
 * Narrow-header replacement for the side-by-side search input + filter
 * combobox. Uses Popcorn's DropdownMenu so the menu auto-renders as a
 * bottom-sheet drawer below 640px (matching the page-header container query).
 * Filter groups only render in the Discover view — Team/Personal expose
 * search only.
 */
const SearchAndFilterNarrow = ({
  query,
  filters,
  groups,
  showFilters,
  onQueryChange,
  onFiltersChange,
  onReset,
}: Omit<TemplatesSearchAndFilterProps, 'cta'>): JSX.Element => {
  const activeCount = filters.length + (query.length > 0 ? 1 : 0)

  return (
    <DropdownMenu modal={false}>
      <DropdownMenu.Trigger>
        <span className={styles.narrowSearchTrigger}>
          <IconButton
            variant="tertiary"
            size="medium"
            label={
              activeCount > 0
                ? `Search and filter (${activeCount} active)`
                : 'Search and filter'
            }
          >
            <SearchIcon />
          </IconButton>
          {activeCount > 0 && (
            <Badge
              variant="brand"
              size="small"
              className={styles.narrowSearchTriggerBadge}
            >
              {activeCount}
            </Badge>
          )}
        </span>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="start" className={styles.narrowSearchMenu}>
        <NarrowMenuTitle>Search and filter templates</NarrowMenuTitle>
        <DropdownMenu.Label className={styles.narrowSearchInputRow}>
          <Input
            type="search"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Search templates…"
            aria-label="Search templates"
            prefix={<SearchIcon />}
          />
        </DropdownMenu.Label>
        {showFilters && (
          <>
            <DropdownMenu.Separator />
            <div className={styles.narrowFilters}>
              {groups.map((group) => (
                <NarrowFilterChipGroup
                  key={group.heading}
                  label={group.heading}
                  options={group.options}
                  filters={filters}
                  onFiltersChange={onFiltersChange}
                />
              ))}
            </div>
            {activeCount > 0 && (
              <>
                <DropdownMenu.Separator />
                <div className={styles.narrowSearchFooter}>
                  <Button variant="tertiary" size="small" onClick={onReset}>
                    Reset Filters
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </DropdownMenu.Content>
    </DropdownMenu>
  )
}

export const TemplatesSearchAndFilter = ({
  query,
  filters,
  groups,
  showFilters,
  cta,
  onQueryChange,
  onFiltersChange,
  onReset,
}: TemplatesSearchAndFilterProps): JSX.Element => {
  // Search starts collapsed as a button; clicking expands it into the input.
  // It stays expanded while focused or while there's an active query.
  const [searchOpen, setSearchOpen] = React.useState(false)
  const searchInputRef = React.useRef<HTMLInputElement>(null)
  const showSearchInput = searchOpen || query.length > 0

  React.useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus()
  }, [searchOpen])

  return (
    <>
      <Flex gap="xs" align="center" className={styles.hideBelowLg}>
        {showSearchInput ? (
          <Input
            ref={searchInputRef}
            type="search"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            onBlur={() => {
              if (!query) setSearchOpen(false)
            }}
            placeholder="Search templates…"
            aria-label="Search templates"
            prefix={<SearchIcon />}
            className={styles.searchInput}
          />
        ) : (
          <Button
            variant="tertiary"
            size="medium"
            onClick={() => setSearchOpen(true)}
          >
            <SearchIcon />
            Search
          </Button>
        )}
        {showFilters && (
          <TemplatesFilterCombobox
            cta={cta}
            value={filters}
            onChange={onFiltersChange}
            onReset={onReset}
          >
            <TemplatesFilterCombobox.Trigger>
              <Button variant="tertiary" size="medium">
                <ListFilterIcon />
                Filter
                <TemplatesFilterCombobox.Counter />
                <ChevronDownIcon />
              </Button>
            </TemplatesFilterCombobox.Trigger>
            <TemplatesFilterCombobox.Content>
              <TemplatesFilterCombobox.List>
                {groups.map((group, index) => (
                  <React.Fragment key={group.heading}>
                    {index > 0 && <TemplatesFilterCombobox.Separator />}
                    <TemplatesFilterCombobox.Group
                      heading={group.heading}
                      filterType={group.filterType}
                    >
                      {group.options.map((option) => (
                        <TemplatesFilterCombobox.Item
                          key={option.value}
                          value={option.value}
                        >
                          {option.label}
                        </TemplatesFilterCombobox.Item>
                      ))}
                    </TemplatesFilterCombobox.Group>
                  </React.Fragment>
                ))}
              </TemplatesFilterCombobox.List>
              <TemplatesFilterCombobox.Footer>
                <TemplatesFilterCombobox.ResetButton>
                  Reset Filters
                </TemplatesFilterCombobox.ResetButton>
              </TemplatesFilterCombobox.Footer>
            </TemplatesFilterCombobox.Content>
          </TemplatesFilterCombobox>
        )}
      </Flex>
      <div className={styles.hideAboveLg}>
        <SearchAndFilterNarrow
          query={query}
          filters={filters}
          groups={groups}
          showFilters={showFilters}
          onQueryChange={onQueryChange}
          onFiltersChange={onFiltersChange}
          onReset={onReset}
        />
      </div>
    </>
  )
}
